<template>
    <div class="doughnut-chart">
        <div class="chart-wrap">
            <DoughnutChart
                :chart-data="chartData"
                :options="options"
                :width="228"
                :height="228"
            />
            <div class="chart-tooltip">
                <span
                    class="tooltip-value"
                    :style="{ color: tooltipColor }"
                >
                    {{ tooltipValue }}
                </span>
                <span class="tooltip-label">
                    {{ tooltipLabel }}
                </span>
            </div>
        </div>
        <chart-legend :data="legendData" />
    </div>
</template>

<script>

/**
 * External dependencies
 */
import { DoughnutChart } from 'vue-chart-3';
import {
    chain,
    findIndex,
    map,
    orderBy,
    sumBy,
} from 'lodash';

/**
 * Internal dependencies
 */
import ChartLegend from './Legend';
import setupChartJs from '@//utils/setupChartJs';

setupChartJs();

export default {
    components: {
        DoughnutChart,
        ChartLegend,
    },

    props: {
        data: {
            required: true,
            type: Array,
        },
        labelKey: {
            required: true,
            type: String,
        },
        valueKey: {
            default: 'total_listens',
            type: String,
        },
        maxItems: {
            default: 7,
            type: Number,
        },
    },

    data: () => ({
        activeItem: {},
        colors: [
            '#632adb',
            '#1072EA',
            '#03B3FF',
            '#1FD7E3',
            '#3AD46F',
            '#B5D852',
            '#D5C53C',
        ],
    }),

    computed: {
        /**
         * Returns computed chart options.
         *
         * @returns {object} Chart options.
         */
        options() {
            return {
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        enabled: false,
                        external: this.tooltip,
                    },
                },
                responsive: true,
                cutout: 96,
                radius: 115,
            };
        },

        /**
         * Returns prepared chart data.
         *
         * @returns {Array|null|boolean} Chart data.
         */
        parsedData() {
            if (! this.data) {
                return this.data;
            }

            const otherRegExp = /^other$/i;

            const otherIndex = findIndex(
                this.data,
                item => otherRegExp.test(item[this.labelKey])
            );

            const data = chain(this.data)
                .reject((item, index) => index === otherIndex)
                .orderBy(this.valueKey, 'desc')
                .map(item => ({
                    label: item[this.labelKey],
                    value: parseInt(item[this.valueKey]),
                }))
                .value();

            const maxIndex = this.maxItems - 1;

            const otherValue =
                (-1 !== otherIndex ? parseInt(this.data[otherIndex][this.valueKey]) : 0) +
                (data.length > this.maxItems ? sumBy(data.slice(maxIndex), 'value') : 0);

            return orderBy([
                ...(data.length > this.maxItems ? data.slice(0, maxIndex) : data),
                ...(otherValue ? [
                    {
                        label: 'Other',
                        value: otherValue,
                    },
                ] : []),
            ], 'value', 'desc');
        },

        /**
         * Returns data for legend.
         *
         * @returns {Array} Legend data.
         */
        legendData() {
            return map(this.parsedData, (item, index) => ({
                ...item,
                color: this.colors[index],
                valueFormatted: this.getPercentFormatted(item.value),
                valuePercent: this.getPercent(item.value),
            }));
        },

        /**
         * Returns prepared chart data.
         *
         * @returns {object} Chart data.
         */
        chartData() {
            return {
                labels: map(this.parsedData, 'label'),
                datasets: [
                    {
                        data: map(this.parsedData, 'value'),
                        backgroundColor: this.colors,
                        borderWidth: 6,
                        hoverBorderColor: '#fff',
                    },
                ],
            };
        },

        /**
         * Returns sum of all items' values.
         *
         * @returns {number} Total value.
         */
        totalValue() {
            return sumBy(this.parsedData, 'value');
        },

        /**
         * Returns tooltip label.
         *
         * @returns {string} Tooltip label.
         */
        tooltipLabel() {
            return this.activeItem?.label || this.parsedData[0]?.label;
        },

        /**
         * Returns tooltip value.
         *
         * @returns {string} Tooltip value.
         */
        tooltipValue() {
            return this.getPercentFormatted(this.activeItem?.value || this.parsedData[0]?.value);
        },

        /**
         * Returns tooltip color.
         *
         * @returns {string} Tooltip color.
         */
        tooltipColor() {
            return this.activeItem?.color || this.colors[0];
        },
    },

    methods: {
        /**
         * Renders chart tooltip.
         *
         * @param  {object} context Tooltip context.
         * @returns {void}
         */
        tooltip({ tooltip }) {
            this.activeItem = 0 === tooltip.opacity ? null : {
                label: tooltip.dataPoints[0]?.label,
                value: tooltip.dataPoints[0]?.parsed,
                color: tooltip.labelColors[0]?.backgroundColor,
            };
        },

        /**
         * Returns percentage value.
         *
         * @param  {number} value Value.
         * @returns {number}      Percentage value.
         */
        getPercent(value) {
            return value / this.totalValue * 100;
        },

        /**
         * Returns percentage value.
         *
         * @param  {number} value Value.
         * @returns {number}      Percentage value.
         */
        getPercentFormatted(value) {
            const percent = this.getPercent(value);
            const formatted = 1 > percent ? '<1' : Math.round(percent);

            return `${formatted}%`;
        },
    },
};
</script>
